import { type FC, memo } from 'react';
import { Link } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronRight, ExpandMore, GridView } from '@mui/icons-material';

import { type AuditLightingCountFormEntry } from '#packages/data-collection/models/audit-lighting-count-form';
import { ControlsLabel } from '#apps/client-web/features/audit-form-lighting-count/constants';
import { type Controls } from '#packages/data-collection/models/audit-lighting-count-form';

export type LightingCountSectionGroup = {
  id: string;
  label: string;
  entries: Array<{
    link: string;
    entry: AuditLightingCountFormEntry;
    fixtureTypeLabel?: string;
  }>;
};

export type AuditFormLightingCountPageRendererProps = {
  groups: LightingCountSectionGroup[];
};

export const AuditFormLightingCountPageRenderer: FC<AuditFormLightingCountPageRendererProps> =
  memo(({ groups }) => (
    <Box flexGrow={1}>
      {groups.map((group) => (
        <Accordion key={group.id} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <GridView fontSize="small" />
              <Typography variant="body1">{group.label}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              {group.entries.map(({ link, entry, fixtureTypeLabel }, index) => (
                <ButtonBase
                  key={link}
                  component={Link}
                  to={link}
                  sx={{ display: 'block', textAlign: 'start' }}>
                  <Grid container alignItems="center" sx={{ p: 2 }}>
                    <Grid size="grow">
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight="bold">
                        {entry.room || '—'}
                      </Typography>
                      {fixtureTypeLabel && (
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary">
                            &nbsp;•&nbsp;
                          </Typography>
                          <Typography component="span" variant="body2">
                            {fixtureTypeLabel}
                          </Typography>
                        </>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                        {entry.fixtureQuantity != null && (
                          <Typography variant="caption" color="textSecondary">
                            {entry.fixtureQuantity} fixtures
                          </Typography>
                        )}
                        {entry.controls && (
                          <Typography variant="caption" color="textSecondary">
                            {ControlsLabel[entry.controls as Controls]}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                    <Grid>
                      <ChevronRight
                        sx={(theme) => ({ color: theme.palette.grey[400] })}
                      />
                    </Grid>
                  </Grid>
                  {index < group.entries.length - 1 && <Divider />}
                </ButtonBase>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  ));
